import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Tooltip } from 'antd';
import { List, ListItemText } from 'appkit-react';
import { UserAvatar } from '@components/commonAvatar';
import { useDispatch, useSelector } from 'react-redux';
import { WeeklyReport } from '@components';
import { useHistory } from 'react-router-dom';
import loginActions from '@flux/action/login';
import actions from '@flux/action';
import { getSessionStorage } from '@utils/windowUtil';
import { isEmailVerifiedOrBypassed } from '@utils/emailVerificationBypass';

function NameInitialsDropdown(props) {
  const emailVerModalRef = useSelector(
    (state) => state.common.emailVerModalRef,
  );
  const userInfo = useSelector((state) => state.login.userInfo);
  const history = useHistory();
  const dispatch = useDispatch();
  const { family_name = 'User', given_name = 'name' } = userInfo || {};
  const nameLabel = given_name[0] + family_name[0];
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);

  const jumpAndCloseTooltip = useCallback((path) => {
    const currentPath = history.location.pathname;
    const namesInitialsPaths = [
      '/settings',
      '/guide',
      '/guide-shared',
      '/issue-list',
      // '/privacy',
    ];
    const pathsNeedBackToProjectHome = [
      '/projects/manageTiles',
      '/projects/imageLink',
      '/projects/pdfLink',
      '/projects/htmlLink',
      '/projects/link',
      '/projects/powerBI',
      '/powerBI',
      '/link',
      ...namesInitialsPaths,
    ];
    const pathNeedBackToProjectList = ['/projects/list', ...namesInitialsPaths];
    if (currentPath !== path) {
      const currentProjectId = getSessionStorage('currentId');
      const needBackToProjectHome =
        currentProjectId &&
        pathsNeedBackToProjectHome.some((i) => currentPath.startsWith(i));
      const needBackToProjectList =
        !currentProjectId &&
        pathNeedBackToProjectList.some((i) => currentPath.startsWith(i));
      let from = {};
      if (needBackToProjectHome) {
        from = {
          path: `/project-detail/${currentProjectId}`,
          state: { preventRefresh: true },
        };
      } else if (needBackToProjectList) {
        from = {
          path: '/projects/list',
          state: {},
        };
      } else {
        from = {
          path: currentPath,
          state: {},
        };
      }
      history.push({
        pathname: path,
        state: { from },
      });
    }
    setTooltipVisible(false);
  }, []);

  const handleSignOut = useCallback(() => {
    dispatch(loginActions.logout());
  }, []);

  const logoutTooltip = useMemo(
    () => (
      <div className="avatar-menu-wrapper">
        <List className="avatar-menu">
          <ListItemText className="avatar-menu-item">
            <div className="user-info-card">
              <UserAvatar className="avatar" userInfo={userInfo} />
              <div className="right">
                <span className="name">
                  {`${userInfo.given_name} `}
                  {userInfo.family_name}
                </span>
                <span className="email">
                  {userInfo.cloudEmail || userInfo.email}
                </span>
              </div>
            </div>
          </ListItemText>
          <ListItemText className="avatar-menu-item">
            {!userInfo.isInternal && !userInfo.isClient && (
              <div
                className="list-item"
                onClick={() => {
                  jumpAndCloseTooltip('/guide');
                }}
              >
                M&A Gateway Reference Guide
              </div>
            )}
            {!userInfo.isInternal && !userInfo.isClient && (
              <div
                className="list-item"
                onClick={() => {
                  jumpAndCloseTooltip('/guide-shared');
                }}
              >
                PowerBI / Tableau Report Configuration Guide
              </div>
            )}
            {userInfo.isAdmin && (
              <div
                className="list-item"
                onClick={() => {
                  if (isEmailVerifiedOrBypassed(userInfo)) {
                    jumpAndCloseTooltip('/issue-list');
                  } else {
                    emailVerModalRef &&
                      emailVerModalRef.show &&
                      emailVerModalRef.show({
                        callback: () => {
                          jumpAndCloseTooltip('/issue-list');
                          dispatch(actions.getUserInfo);
                        },
                      });
                  }
                }}
              >
                Issue List
              </div>
            )}
            <div
              className="list-item"
                // onClick={() => {
                //   jumpAndCloseTooltip('/privacy');
                // }}
                onClick={() => {
                  window.open('https://www.pwc.com/us/en/site/privacy.html', '_blank');
                }}
            >
              Privacy Statement
            </div>
            {(userInfo.isInternal || userInfo.isAdmin) && (
              <div
                className="list-item"
                onClick={() => {
                  window.open(
                    'https://pwc.sharepoint.com/:p:/s/US-ADV-SolutionInnovations/Ed8yhMoHTp9Dso7W5QYGj78B6oOSiPTEc-AkqgGI9hx1dA',
                    '_blank',
                  );
                }}
              >
                M&A Gateway Getting Started Guide
              </div>
            )}
            {userInfo.isAdmin && (
              <>
                <div
                  className="list-item"
                  onClick={() => {
                    setShowWeeklyReport(true);
                  }}
                >
                  Weekly Project Report Download
                </div>
                <div
                  className="list-item"
                  onClick={() => {
                    if (isEmailVerifiedOrBypassed(userInfo)) {
                      jumpAndCloseTooltip('/settings');
                    } else {
                      emailVerModalRef &&
                        emailVerModalRef.show &&
                        emailVerModalRef.show({
                          callback: () => {
                            jumpAndCloseTooltip('/settings');
                            dispatch(actions.getUserInfo);
                          },
                        });
                    }
                  }}
                >
                  System Settings
                </div>
              </>
            )}
            {userInfo && userInfo.isClient && (
              <div
                className="list-item"
                onClick={() => {
                  if (process.env) {
                    window.open(
                      process.env.REACT_APP_change_password_url,
                      '_blank',
                    );
                  }
                }}
              >
                Reset Password
              </div>
            )}
          </ListItemText>
          <ListItemText className="avatar-menu-item">
            <div
              className="avatar-menu-bottom capitalize-title"
              onClick={handleSignOut}
            >
              sign out
            </div>
          </ListItemText>
        </List>
      </div>
    ),
    [userInfo],
  );

  useEffect(() => {
    const hideTooltip = () => {
      setTooltipVisible(false);
    };
    history.listen(hideTooltip);

    document.addEventListener('click', hideTooltip);
    return () => {
      document.removeEventListener('click', hideTooltip);
    };
  }, []);

  return (
    <>
      <Tooltip
        title={logoutTooltip}
        placement="bottomRight"
        trigger="click"
        getPopupContainer={() =>
          document.querySelector('.app-header .avatar-wrapper') ||
          document.querySelector('.error-page-wrapper')
        }
        onClick={(e) => {
          e.nativeEvent.stopImmediatePropagation();
          setTooltipVisible((visible) => !visible);
        }}
        visible={tooltipVisible}
        overlayClassName="avatar-overlay"
      >
        <UserAvatar
          className="avatar"
          size="default"
          shape="circle"
          label={nameLabel}
          userInfo={userInfo}
          // onClick={(e) => {
          //   e.nativeEvent.stopImmediatePropagation();
          //   this.setState((prevState) => ({
          //     isTooltipVisible: !prevState.isTooltipVisible,
          //   }));
          // }}
        />
      </Tooltip>
      {showWeeklyReport ? (
        <WeeklyReport
          className="weekly-report-modal"
          visible={showWeeklyReport}
          title="Weekly Project Report Download"
          okText="DOWNLOAD"
          onCancel={() => {
            setShowWeeklyReport(false);
          }}
        />
      ) : null}
    </>
  );
}

export default NameInitialsDropdown;
